import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { retryText } from "@/lib/stageGate";

// R13 Workstream 7 - the shared, persistent explanation of a refused stage move. It replaces the toast
// the Pipeline table, Kanban and Case stage rail used to show (and that vanished before it could be
// read). Everything shown comes from the backend's structured refusal - nothing is derived here.
// `action` ({label, run}) is the existing purpose-built remedy (the outcome form), offered FROM the explanation:
// the person first sees what was refused and why, then opens the form with one click.
export const StageGateExplanation = ({ view, onClose, onOpenCase, action = null }) => {
  if (!view) return null;
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-lg" data-testid="stage-gate-explanation">
        <DialogHeader>
          <DialogTitle className="font-heading" data-testid="stage-gate-title">
            {view.requested ? `Can't move to ${view.requested}` : "This move was refused"}
          </DialogTitle>
          <DialogDescription data-testid="stage-gate-transition">
            {view.subject ? `${view.subject}: ` : ""}
            {view.current ? <>currently at <b>{view.current}</b>{view.requested ? <> → requested <b>{view.requested}</b></> : null}</>
              : "The record was left exactly as it was."}
            {" "}Nothing was changed.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          {view.invariant && (
            <p data-testid="stage-gate-invariant"><b>Why:</b> {view.invariant}</p>
          )}
          <p data-testid="stage-gate-message">{view.message}</p>
          {(view.missingTasks.length > 0 || view.missingItems.length > 0) && (
            <div data-testid="stage-gate-missing">
              <p className="font-medium">Still missing{view.missingKind ? ` (${view.missingKind})` : ""}:</p>
              <ul className="list-disc pl-5 space-y-0.5">
                {view.missingTasks.map((t) => <li key={`t-${t}`}>{t}</li>)}
                {view.missingItems.map((t) => <li key={`i-${t}`}>{t}</li>)}
              </ul>
            </div>
          )}
          {view.nextAction && (
            <p data-testid="stage-gate-next-action"><b>Next:</b> {view.nextAction}</p>
          )}
          {view.permitted && view.permitted.length > 0 && (
            <p data-testid="stage-gate-permitted"><b>Allowed from here:</b> {view.permitted.join(", ")}</p>
          )}
          <p className="text-xs text-muted-foreground" data-testid="stage-gate-retry">{retryText(view)}</p>
        </div>
        <DialogFooter className="gap-2">
          {view.existingCaseId && onOpenCase && (
            <Button type="button" variant="outline" data-testid="stage-gate-open-case"
              onClick={() => onOpenCase(view.existingCaseId)}>Open Case {view.existingCaseId}</Button>
          )}
          {action && (
            <Button type="button" variant="outline" data-testid="stage-gate-action"
              onClick={() => { onClose(); action.run(); }}>{action.label}</Button>
          )}
          <Button type="button" data-testid="stage-gate-close" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
